"use client";

import React, { useEffect, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { Search as SearchIcon } from 'lucide-react';

export default function Search() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      if (!containerRef.current) return;
      if (target && !containerRef.current.contains(target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick);

    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
    };
  }, [open]);

  const pathname = usePathname() || '/';
  const router = useRouter();
  const currentLocale = (() => {
    const seg = pathname.split('/')[1]?.toLowerCase();
    return seg || 'id';
  })();

  const onSubmitSearch = () => {
    if (!q) return setOpen((v) => !v);
    router.push(`/${currentLocale}/search?q=${encodeURIComponent(q)}`);
    setOpen(false);
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter') {
      onSubmitSearch();
    }
    if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="flex items-center relative">
      <div className="flex items-center">
        <div
          className={`overflow-hidden transition-all duration-300 flex items-center ${open ? 'max-w-[45vw] sm:max-w-[320px] opacity-100' : 'max-w-0 opacity-0'}`}
        >
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search"
            className="w-full border-0 border-b border-gray-300 focus:outline-none focus:border-gray-500 pb-1 bg-transparent"
          />
        </div>

        <button
          onClick={() => {
            if (open && q) return onSubmitSearch();
            setOpen((v) => !v);
          }}
          aria-label="Open search"
          className="p-1 hover:bg-gray-100 rounded ml-2"
        >
          <SearchIcon color='#124673'/>
        </button>
      </div>

    </div>
  );
}
